import createClient from 'openapi-fetch'
import type { components, paths } from './schema'

/**
 * The typed API client.
 *
 * Every request and response type comes from `./schema.d.ts`, which is
 * generated from FastAPI's OpenAPI document by `scripts/generate-client.sh`.
 * Nothing in this workspace declares an API type by hand — hand-written DTOs
 * drift from the server and defeat the reason FastAPI was chosen
 * (REQUIREMENTS §9.1). CI regenerates and fails on any difference.
 *
 * The base URL is this page's own origin, never a configured hostname. The SPA
 * and `/api/*` are served by one CloudFront distribution per host, so the API is
 * same-origin by construction (REQUIREMENTS §9.1) — which is what removes CORS
 * from the picture entirely and lets the session cookie work without cross-site
 * rules. The local dev server proxies /api to uvicorn so the same is true there.
 *
 * `window.location.origin` rather than a bare `'/'`: `fetch` outside a browser —
 * jsdom under vitest, or any future prerender — rejects a relative URL with
 * "Invalid URL", and a client that only works in one runtime is a client whose
 * tests prove nothing.
 */
export const api = createClient<paths>({
  baseUrl: window.location.origin,
  // Resolved per call rather than captured at module load. openapi-fetch reads
  // `globalThis.fetch` once when the client is created, which makes the client
  // untestable — a stub installed by a test arrives too late to be used.
  fetch: (request: Request) => globalThis.fetch(request),
})

/** Shorthand for a generated response model, so no page restates one by hand. */
export type Schema<K extends keyof components['schemas']> = components['schemas'][K]

/**
 * The one error shape the API returns (ENGINEERING-STANDARDS §4).
 *
 * Aliased from the generated schema rather than restated: the envelope is part
 * of the API contract like any response model, and a hand-written copy would be
 * the one API type in this workspace that CI could not catch drifting.
 */
export type ApiErrorEnvelope = Schema<'ErrorEnvelope'>

export function isApiError(value: unknown): value is ApiErrorEnvelope {
  if (typeof value !== 'object' || value === null || !('error' in value)) return false
  const { error } = value
  return (
    typeof error === 'object' &&
    error !== null &&
    'code' in error &&
    'message' in error &&
    'request_id' in error
  )
}

/**
 * The code a failure carries when the request never reached the API at all — a
 * dropped connection, DNS, a CloudFront error page. Not an `ErrorCode`, and
 * deliberately not spelled like one: there is no envelope and no request id to
 * quote, so a screen must not offer one.
 */
export const NETWORK_FAILURE = 'NETWORK_FAILURE'

/** A failure, normalised so every screen handles one shape. */
export interface ApiFailure {
  /** An `ErrorCode` from the envelope, or `NETWORK_FAILURE`. */
  code: string
  /** Safe to show a user. */
  message: string
  /** Empty when the request never reached the API. */
  requestId: string
  /** 0 when the request never reached the API. */
  status: number
  /**
   * The `Retry-After` header, in seconds, when the API sent one.
   *
   * Lifted out of the header rather than parsed from the message, because the
   * number is what the screens need and the message is prose: the account-lock
   * screen counts it down and the OTP resend button is disabled for it. Both are
   * `ACCOUNT_LOCKED` and `RATE_LIMITED` responses that already carry the header
   * (`app/v1/auth.py`), so this is reading what is sent, not asking for more.
   */
  retryAfterSeconds: number | null
  /**
   * Per-field validation refusals, when the envelope carried them.
   *
   * Empty for everything but a `VALIDATION_FAILED` 422, which is the only code
   * that sends `error.details.fields` today. Read here rather than at each
   * screen because the envelope is normalised in exactly one place, and a
   * screen that had to reach into `details` itself would be the second place
   * that knows the wire shape.
   */
  fields: readonly { loc: readonly string[]; msg: string }[]
  /**
   * The workspace address the rule would accept instead, when the envelope
   * carried one — `WORKSPACE_MISMATCH` on the signup, where it is the business
   * email's own domain label. Normalised here for `fields`' reason.
   */
  suggestion: string | null
}

export type ApiResult<T> = { ok: true; data: T } | { ok: false; failure: ApiFailure }

/**
 * A message safe to show a user, and the id support needs.
 *
 * Falls back to a generic message when the failure never reached the API,
 * because in that case there is no envelope and no request id to quote.
 */
export function describeError(value: unknown, status = 0, response?: Response): ApiFailure {
  const retryAfterSeconds = retryAfter(response)
  if (isApiError(value)) {
    return {
      code: value.error.code,
      message: value.error.message,
      requestId: value.error.request_id,
      status,
      retryAfterSeconds,
      // `details.fields` is declared on the envelope (`app/errors.ErrorDetails`)
      // and present only on a 422. Everything else gets an empty list, so a
      // caller never has to distinguish "no fields" from "not that kind of
      // failure" — both mean there is nothing per-field to show.
      fields: value.error.details?.fields ?? [],
      suggestion: value.error.details?.suggestion ?? null,
    }
  }
  return {
    code: NETWORK_FAILURE,
    message: 'Could not reach the server. Check your connection and try again.',
    requestId: '',
    status,
    retryAfterSeconds,
    fields: [],
    suggestion: null,
  }
}

/**
 * `Retry-After` as a number of seconds, or null.
 *
 * Only the delta-seconds form is read. RFC 9110 also allows an HTTP-date, and
 * AugRank never sends one — accepting it would mean trusting the client clock to
 * agree with the server's, and a browser five minutes fast would count a lock
 * down to zero while the server still refused.
 */
function retryAfter(response: Response | undefined): number | null {
  const header = response?.headers.get('Retry-After')
  if (!header) return null
  const seconds = Number(header.trim())
  return Number.isFinite(seconds) && seconds >= 0 ? seconds : null
}

/**
 * What openapi-fetch resolves to. Restated structurally rather than imported:
 * its own type is a discriminated union with `never` arms, and `never` is
 * assignable to anything, so this accepts every generated call while staying a
 * shape `call()` can read without a cast.
 */
interface FetchOutcome<T> {
  data?: T
  error?: unknown
  response: Response
}

/** Listeners notified when the API says the session is gone. */
type ExpiryListener = () => void
const expiryListeners = new Set<ExpiryListener>()

/**
 * Subscribe to "the session this browser holds is no longer valid".
 *
 * A module-level registry rather than a React context, because the signal
 * originates in `call()` — which any page may reach, including from an effect
 * that outlives the component that started it. The auth layer subscribes once
 * (`AuthProvider`) and everything else stays unaware of the mechanism.
 *
 * Returns its own unsubscribe.
 */
export function onSessionExpired(listener: ExpiryListener): () => void {
  expiryListeners.add(listener)
  return () => {
    expiryListeners.delete(listener)
  }
}

/**
 * Run one generated call and normalise every way it can end.
 *
 * Four outcomes have to collapse into two, and doing it here rather than in
 * every screen is the point:
 *
 *  * the envelope the API returns for a handled error,
 *  * a rejected `fetch` — no envelope, no request id, because nothing answered,
 *  * a 204, where success carries no body at all and `data` is `undefined`,
 *  * a 401, which is also the signal that this browser's session is gone.
 *
 * `AbortError` is re-thrown rather than reported: an aborted request is a
 * component that unmounted, and reporting it would put "could not reach
 * AugRank" on screens the user has already navigated away from.
 */
export async function call<T>(run: () => Promise<FetchOutcome<T>>): Promise<ApiResult<T>> {
  let outcome: FetchOutcome<T>
  try {
    outcome = await run()
  } catch (thrown) {
    if (thrown instanceof DOMException && thrown.name === 'AbortError') throw thrown
    return { ok: false, failure: describeError(thrown) }
  }

  if (outcome.response.ok) {
    // A 204 has no body, so `data` is `undefined` and that *is* the success
    // value — the generated signature for such a route already says so.
    return { ok: true, data: outcome.data as T }
  }

  const failure = describeError(outcome.error, outcome.response.status, outcome.response)
  if (outcome.response.status === 401) {
    for (const listener of expiryListeners) listener()
  }
  return { ok: false, failure }
}
